import { PaymentDetail } from "@features/cx/payments/types";
import { PaymentDetailsModal } from "@features/cx/paymentsv2/components/Detail/PaymentDetail";
import { useReconciliationDrillDownExport } from "@features/cx/paymentsv2/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Eye } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface DrillDownRow {
  accountNo: string;
  consumerId: number;
  consumerName: string;
  linkedPaymentCount: number;
  totalAmount: string;
  paymentDate: string;
  mode: string;
  channelDisplay: string;
  channel: string;
  counterId: string;
  paymentChannel: string;
  paymentIds: number[];
}

interface DrillDownData {
  results?: DrillDownRow[];
  count?: number;
  channelDisplay?: string;
  kpis?: unknown[];
}

interface MappedDrillDownRow {
  id: number | undefined;
  accountNo: string;
  consumerId: number;
  consumerName: string;
  linkedPaymentCount: number;
  totalAmount: string;
  paymentDate: string;
  mode: string;
  channelDisplay: string;
  channel: string;
  counterId: string;
  paymentChannel: string;
  paymentIds: number[];
}

export const DrillDownList = ({
  drillDownData,
  isLoading,
  currentPage = 1,
  limit = 10,
  onPageChange,
  onSearch,
}: {
  drillDownData: DrillDownData | undefined;
  isLoading?: boolean;
  currentPage?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onSearch?: (searchTerm: string) => void;
}) => {
  const currency = useCurrency();
  const [selectedPayment, setSelectedPayment] = useState<PaymentDetail | null>(
    null,
  );
  const [isPaymentDetailOpen, setIsPaymentDetailOpen] = useState(false);
  const { id, channelId } = useParams<{ id: string; channelId: string }>();
  const { exportCSV, isExporting } = useReconciliationDrillDownExport(
    id || "",
    channelId || "",
  );

  // Format the snake_case API response to camelCase
  const formattedData = useMemo<MappedDrillDownRow[]>(() => {
    return (drillDownData?.results || []).map((item: any) => ({
      id: item.paymentId, // Correctly map the payment ID
      accountNo: item.accountNo || "N/A",
      consumerId: item.consumerId || "N/A",
      consumerName: item.consumerName || "N/A",
      linkedPaymentCount: item.linkedPaymentCount || "N/A",
      totalAmount: item.totalAmount || "N/A",
      paymentDate: item.paymentDate || "N/A",
      mode: item.mode,
      channelDisplay: item.channelDisplay || "N/A",
      channel: item.channel || "N/A",
      counterId: item.counterId || "N/A",
      paymentChannel: item.paymentChannel || "N/A",
      paymentIds: item.paymentIds || [],
    }));
  }, [drillDownData]);

  const columns: TableColumn<MappedDrillDownRow>[] = useMemo(
    () => [
      {
        key: "accountNo",
        header: "Account No",
      },
      {
        key: "consumerName",
        header: "Consumer Name",
      },
      {
        key: "linkedPaymentCount",
        header: "Linked Payment",
      },
      {
        key: "totalAmount",
        header: `Amount (${currency})`,
      },
      {
        key: "paymentDate",
        header: "Payment Date",
      },
      {
        key: "mode",
        header: "Mode",
      },
    ],
    [currency],
  );

  const handleViewPaymentDetails = (payment: MappedDrillDownRow) => {
    setSelectedPayment(payment as unknown as PaymentDetail);
    setIsPaymentDetailOpen(true);
  };

  const actions: TableAction<MappedDrillDownRow>[] = [
    {
      label: "Detail",
      icon: <Eye className="w-4 h-4" />,
      onClick: (row: MappedDrillDownRow) => handleViewPaymentDetails(row),
      variant: "outline",
    },
  ];

  const totalCount = drillDownData?.count || 0;
  const pagination = useMemo(() => {
    if (!drillDownData) return undefined;
    return {
      currentPage,
      totalPages: Math.ceil(totalCount / limit) || 1,
      totalItems: totalCount,
      itemsPerPage: limit,
      startItem: (currentPage - 1) * limit + 1,
      endItem: Math.min(currentPage * limit, totalCount),
    };
  }, [currentPage, totalCount, limit, drillDownData]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col">
      {/* Table Content */}
      <div className="p-4">
        <GenericTable
          data={formattedData}
          columns={columns}
          actions={actions}
          actionsType="icons"
          rowKey="id"
          loading={isLoading}
          pagination={pagination}
          onPageChange={onPageChange}
          searchPlaceholder="Search by consumer, account, or reference..."
          onSearch={onSearch}
          onExport={exportCSV}
          exportLoading={isExporting}
        />
      </div>

      {/* Payment Detail Dialog */}
      <PaymentDetailsModal
        isOpen={isPaymentDetailOpen}
        onClose={() => setIsPaymentDetailOpen(false)}
        payment={selectedPayment}
      />
    </div>
  );
};

export default DrillDownList;
